import { useState } from 'react';
import { Link } from 'wouter';
import {
  api, type ByItem, type FinanceCustomers, type FinanceD1, type FinanceMonth, type FinanceMonthly, type FinanceStatement,
} from '../lib/api';
import type { ConfirmRequest } from '../components/Confirm';
import { useResource } from '../lib/use-resource';
import { whenLocal } from '../lib/time';
import {
  aud, nzd, audAtRate, rateLabel, column, chargesApplied, marginPct, delta, monthLong, monthShort,
  balancesObserved, debtSplit, rows as rowsOf, type Mode,
} from '../lib/finance';
import './finance.css';

/**
 * Is the business moving forward? One Hero billing month per view (the New
 * Zealand calendar month Hero bills in), from finance_monthly as the ledger
 * rebuilds it every night. Nothing here is computed beyond choosing a column:
 * the verdict is lib/finance/verdict.js in the PSA repo, a rule, not a model.
 *
 * EVERY FIGURE NAMES ITS QUERY. Tap or hover any number and the SQL that
 * produced it appears at the foot of the screen. A finance screen that cannot
 * say where a number came from is one the operator has to take on trust.
 */
export function Finance({ onConfirm }: { onConfirm: (r: ConfirmRequest) => void }) {
  const [mode, setMode] = useState<Mode>('ex');
  const [picked, setPicked] = useState<string | null>(null);
  const [shown, setShown] = useState<{ name: string; sql: string } | null>(null);

  const monthly = useResource<FinanceMonthly>(() => api.finance.monthly(picked ?? undefined), { pollMs: 300_000, deps: [picked] });
  const period = picked ?? monthly.data?.verdict?.month ?? null;
  const on = { enabled: Boolean(period), deps: [period] };
  const byItem = useResource<ByItem>(() => api.finance.byItem(period!), on);
  const customers = useResource<FinanceCustomers>(() => api.finance.customers(period!), on);
  const statement = useResource<FinanceStatement>(() => api.finance.statement(period!), on);
  const d1 = useResource<FinanceD1>(() => api.finance.d1(), { pollMs: 300_000 });

  if (monthly.loading && !monthly.data) return <main className="fin"><p className="list-empty">Reading the months…</p></main>;
  if (!monthly.data) return <main className="fin"><p className="list-empty list-error">{monthly.error?.message}</p></main>;

  const m = monthly.data;
  if (!m.rows.length || !period) {
    return <main className="fin"><BuildNow onBuilt={monthly.refetch} /></main>;
  }
  const i = m.rows.findIndex((r) => r.month === period);
  const row = m.rows[i] ?? null;
  const prev = m.rows[i - 1] ?? null;
  const current = m.rows[m.rows.length - 1].month;
  const choices = [...m.rows.slice(-4, -1).reverse().map((r) => r.month), current];
  const v = m.verdict;
  const Q = (name: string, sql: string | undefined) => ({
    title: sql, onClick: () => sql && setShown({ name, sql }), className: 'fin-fig',
  });
  const refetchAll = () => { monthly.refetch(); byItem.refetch(); customers.refetch(); statement.refetch(); };

  return (
    <main className="fin">
      <header className="fin-head">
        <div>
          <div className="fin-eyebrow mono">Finance · Hero billing month</div>
          <h1>{monthLong(period)}{v?.month === period ? ` · ${v.verdict}` : ''}</h1>
          {v?.month === period && <p className="fin-sentence">{v.sentence}</p>}
          {v?.concern && v.month === period && <p className="fin-concern">{v.concern.text}</p>}
        </div>
        <div className="fin-switches">
          <div className="fin-seg mono" role="group" aria-label="Hero billing month">
            {choices.map((c) => (
              <button key={c} type="button" aria-pressed={c === period} onClick={() => setPicked(c)}>
                {c === current ? `${monthShort(c)} so far` : monthShort(c)}
              </button>
            ))}
          </div>
          <div className="fin-seg mono" role="group" aria-label="GST">
            <button type="button" aria-pressed={mode === 'ex'} onClick={() => setMode('ex')}>AUD · ex GST</button>
            <button type="button" aria-pressed={mode === 'inc'} onClick={() => setMode('inc')}>inc</button>
          </div>
        </div>
      </header>

      <Cards row={row} prev={prev} mode={mode} byItem={byItem.data} q={(n) => Q(n, m.queries[n])} />
      <D1Strip data={d1.data} error={d1.error?.message} q={(n) => Q(n, d1.data?.queries[n])} />

      <div className="fin-middle">
        <Trend rows={m.rows} period={period} q={(n) => Q(n, m.queries[n])} />
        <Items data={byItem.data} error={byItem.error?.message} mode={mode} period={period}
          q={(n) => Q(n, byItem.data?.queries[n])} />
      </div>

      <div className="fin-bottom">
        <Balances row={row} period={period} mode={mode} q={(n) => Q(n, m.queries[n])} />
        <Customers data={customers.data} row={row} q={(n) => Q(n, customers.data?.queries[n] ?? m.queries[n])} />
        <Trials data={customers.data} q={(n) => Q(n, customers.data?.queries[n])} />
        <Audit data={byItem.data} />
      </div>

      <Footer data={statement.data} row={row} onConfirm={onConfirm} onChanged={refetchAll}
        q={(n) => Q(n, statement.data?.queries[n])} />

      {shown && (
        <section className="fin-query" aria-live="polite">
          <div className="fin-query-head">
            <span className="mono">{shown.name}</span>
            <button type="button" onClick={() => setShown(null)}>Close</button>
          </div>
          <pre>{shown.sql.replace(/\s+\n/g, '\n')}</pre>
        </section>
      )}
    </main>
  );
}

type Qf = (name: string) => { title?: string; onClick: () => void; className: string };

function Card({ label, value, sub, tone, accent, q }: {
  label: string; value: React.ReactNode; sub: React.ReactNode; tone?: 'up' | 'down' | 'flat' | null; accent?: boolean;
  q: ReturnType<Qf>;
}) {
  return (
    <div className={`fin-card ${accent ? 'is-accent' : ''}`}>
      <div className="fin-label mono">{label}</div>
      <button type="button" {...q}><span className="fin-big">{value}</span></button>
      <div className={`fin-sub ${tone ? `is-${tone}` : ''}`}>{sub}</div>
    </div>
  );
}

function Cards({ row, prev, mode, byItem, q }: { row: FinanceMonth | null; prev: FinanceMonth | null; mode: Mode; byItem: ByItem | null; q: Qf }) {
  const cash = column(row, 'cash_in', mode);
  const dCash = delta(row?.cash_in_inc ?? null, prev?.cash_in_inc ?? null);
  const charges = chargesApplied(row, mode);
  const dCharges = delta(charges, chargesApplied(prev, mode));
  const rate = row?.fx_rate ?? null;
  const heroAud = mode === 'ex' ? byItem?.total.hero_aud_ex : byItem?.total.hero_aud_inc;
  const pct = marginPct(row);
  const dMargin = delta(pct, marginPct(prev), { points: true });
  const dPaying = delta(row?.paying_active ?? null, prev?.paying_active ?? null);
  const dArpu = delta(row?.arpu_aud_ex ?? null, prev?.arpu_aud_ex ?? null);
  const vs = prev ? ` vs ${monthShort(prev.month)}` : '';
  const pn = (n: number | null | undefined) => (n == null ? '?' : n);
  return (
    <div className="fin-cards">
      <Card label="Cash in" q={q('cash_in')} value={aud(cash.value)} tone={dCash?.tone}
        sub={<>{dCash ? `${dCash.text}${vs} · ` : ''}{row?.cash_in_source === 'stripe' ? 'Stripe' : 'portal payments'}
          {row?.cash_in_source === 'stripe' && row.cash_in_portal_inc != null && row.cash_in_portal_inc !== row.cash_in_inc
            ? ` · portal ${aud(row.cash_in_portal_inc)}` : ''}{cash.incOnly ? ' · inc only' : ''}</>} />
      <Card label="Collectable charges" q={q('charges')} value={aud(charges)} tone={dCharges?.tone}
        sub={<>{dCharges ? `${dCharges.text} · ` : ''}recurring {aud(column(row, 'charges_recurring', mode).value)} · usage {aud(column(row, 'charges_usage', mode).value)}
          {column(row, 'charges_uncollectable', mode).value ? <> · less {aud(column(row, 'charges_uncollectable', mode).value)} to cancelled accounts</> : null}
          {column(row, 'charges_trial_credit', mode).value ? <> · less {aud(column(row, 'charges_trial_credit', mode).value)} trial credit consumed</> : null}</>} />
      <Card label="Wholesale (Hero)" q={q('wholesale')} value={audAtRate(heroAud, rate)}
        sub={<>{nzd(mode === 'ex' ? row?.wholesale_nzd_ex_portal : row?.wholesale_nzd_inc)} {mode} · {rateLabel(rate, row?.fx_source ?? null, row?.fx_effective_date ?? null)}</>} />
      <Card label="Gross margin" q={q('charges')} accent tone={dMargin?.tone}
        value={<>{audAtRate(row?.gross_margin_aud, rate)} {pct != null && <span className="fin-pct">{(pct * 100).toFixed(1)}%</span>}</>}
        sub={<>{dMargin ? `${dMargin.text}${vs} · ` : ''}basis: {row?.wholesale_cost_basis ?? '?'} NZ GST{row?.payment_fees_ex != null ? ` · card fees ${aud(row.payment_fees_ex, { cents: true })}` : ''}</>} />
      <Card label="Paying customers" q={q('customers')} value={row?.paying_active ?? 'not observed'} tone={dPaying?.tone}
        sub={row?.paying_active == null ? 'balances and status observed from 22 Sep 2026'
          : <>{row.net != null ? `${row.net >= 0 ? '▲' : '▼'} ${Math.abs(row.net)} net · ` : ''}+{pn(row.converted)} converted · −{pn(row.cancelled)} cancelled</>} />
      <Card label="ARPU" q={q('customers')} value={row?.arpu_aud_ex == null ? '—' : aud(row.arpu_aud_ex, { cents: true })} tone={dArpu?.tone}
        sub={<>{dArpu ? `${dArpu.text} · ` : ''}charges ÷ paying customers, ex</>} />
    </div>
  );
}

/**
 * What the database costs (PSA #290, FIN-52): D1 rows the scheduled runs read, today and over Cloudflare's billing
 * period, the month projected from the days counted, and the bill past the included rows. Cloudflare's own figure also
 * counts HTTP requests, staging and the account's other databases; the strip says so.
 */
function D1Strip({ data, error, q }: { data: FinanceD1 | null; error?: string; q: Qf }) {
  if (!data) return <section className="fin-d1 mono fin-small">{error ? `D1: ${error}` : 'Reading D1 rows…'}</section>;
  const peak = Math.max(1, data.day_limit, ...data.days.map((d) => d.rows_read));
  const over = data.days.some((d) => d.rows_read > data.day_limit);
  const day = (s: string) => new Date(`${s.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return (
    <section className={`fin-d1 ${over ? 'is-alert' : ''}`} aria-label="D1 database rows read">
      <div className="fin-label mono">D1 database · rows read</div>
      <div className="fin-d1-figs">
        <span><button type="button" {...q('d1_by_day')}><b>{rowsOf(data.today)}</b></button> today</span>
        <span><button type="button" {...q('d1_period')}><b>{rowsOf(data.period_rows)}</b></button> since {day(data.period.start)}</span>
        <span><b>{data.projected_rows == null ? 'not yet' : rowsOf(data.projected_rows)}</b> projected to {day(data.period.end)}</span>
        <span><b>{data.projected_usd == null ? '—' : data.projected_usd === 0 ? '$0' : `US$${data.projected_usd.toFixed(2)}`}</b>
          {' '}bill · {rowsOf(data.included_rows)} included</span>
      </div>
      <div className="fin-d1-days" role="img" aria-label={`Rows read each day since ${day(data.period.start)}; the line is the daily limit`}>
        {data.days.map((d) => (
          <i key={d.day} title={`${d.day}: ${rowsOf(d.rows_read)} in ${d.runs} runs`}
            className={d.rows_read > data.day_limit ? 'is-over' : ''} style={{ height: `${Math.max(2, (d.rows_read / peak) * 100)}%` }} />
        ))}
        <span className="fin-d1-limit" style={{ bottom: `${(data.day_limit / peak) * 100}%` }} />
      </div>
      <div className="fin-small">
        Limit {rowsOf(data.day_limit)} a day · counts {data.counts}
        {data.period.counted_from ? ` · counted from ${data.period.counted_from.slice(0, 16)} UTC` : ' · no run counted yet'}
      </div>
    </section>
  );
}

function Trend({ rows, period, q }: { rows: FinanceMonth[]; period: string; q: Qf }) {
  const W = 820; const H = 170; const g = W / Math.max(rows.length, 1);
  const vals = rows.flatMap((r) => [r.cash_in_inc ?? 0, chargesApplied(r) ?? 0, r.wholesale_aud ?? 0]);
  const max = Math.max(1, ...vals);
  const y = (n: number | null) => H - ((n ?? 0) / max) * (H - 20);
  const bar = (n: number | null) => H - y(n);
  const line = rows.map((r, k) => [k, marginPct(r)] as const).filter(([, p]) => p != null)
    .map(([k, p]) => [k * g + g / 2, 150 - Math.max(0, Math.min(1, p as number)) * 120] as const);
  return (
    <section className="fin-panel">
      <div className="fin-panel-head">
        <h2>Forward or backward · {rows.length} Hero months</h2>
        <span className="mono fin-small">bars: cash in · charges · wholesale (AUD) — line: margin %</span>
      </div>
      <button type="button" {...q('cash_in')} aria-label="The twelve months' figures and their queries">
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} preserveAspectRatio="none" aria-hidden="true">
          {rows.map((r, k) => !balancesObserved(r.month) && (
            <rect key={`n${r.month}`} x={k * g} y={0} width={g} height={H} className="fin-unobserved"><title>balances not observed</title></rect>
          ))}
          {[40, 85, 130].map((gy) => <line key={gy} x1={0} y1={gy} x2={W} y2={gy} className="fin-grid" />)}
          {rows.map((r, k) => (
            <g key={r.month}>
              <rect x={k * g + 6} y={y(r.cash_in_inc)} width={12} height={bar(r.cash_in_inc)} className="fin-bar-cash" />
              <rect x={k * g + 20} y={y(chargesApplied(r))} width={12} height={bar(chargesApplied(r))} className="fin-bar-charges" />
              <rect x={k * g + 34} y={y(r.wholesale_aud)} width={12} height={bar(r.wholesale_aud)} className="fin-bar-hero" />
            </g>
          ))}
          {line.length > 1 && <polyline points={line.map(([x, yy]) => `${x},${yy}`).join(' ')} className="fin-line" />}
          {line.map(([x, yy]) => <circle key={x} cx={x} cy={yy} r={3} className="fin-dot" />)}
        </svg>
      </button>
      <div className="fin-months mono" style={{ gridTemplateColumns: `repeat(${rows.length}, minmax(0, 1fr))` }}>
        {rows.map((r) => (
          <span key={r.month} className={`${r.month === period ? 'is-current' : ''} ${balancesObserved(r.month) ? '' : 'is-unobserved'}`}
            title={balancesObserved(r.month) ? undefined : 'balances not observed'}>{monthShort(r.month)}</span>
        ))}
      </div>
      <div className="fin-legend mono">
        <span><i className="fin-key fin-bar-cash" />cash in</span>
        <span><i className="fin-key fin-bar-charges" />collectable charges</span>
        <span><i className="fin-key fin-bar-hero" />wholesale, AUD at the month's rate</span>
        <span><i className="fin-key-line" />gross margin %</span>
        <span className="fin-legend-note">shaded: balances not observed (before 22 Sep 2026)</span>
      </div>
    </section>
  );
}

function Items({ data, error, mode, period, q }: { data: ByItem | null; error?: string; mode: Mode; period: string; q: Qf }) {
  if (!data) return <section className="fin-panel"><p className="list-empty">{error ?? 'Reading the items…'}</p></section>;
  const rate = data.rate.value;
  const hero = (r: { hero_aud_ex: number | null; hero_aud_inc: number | null }) => (mode === 'ex' ? r.hero_aud_ex : r.hero_aud_inc);
  const b = data.beneath;
  return (
    <section className="fin-panel">
      <div className="fin-panel-head">
        <h2>Where {monthLong(period).split(' ')[0]}'s money came from</h2>
        <span className="mono fin-small">retail vs Hero and card fees, by item · {rateLabel(rate, data.rate.source, data.rate.effective_date)}</span>
      </div>
      <table className="fin-table">
        <thead className="mono"><tr><th>Item</th><th>Charged</th><th>Cost</th><th>Margin</th></tr></thead>
        <tbody>
          {data.items.map((it) => (
            <tr key={it.key}>
              <td>{it.label}{it.hero_lines ? <span className="fin-muted"> · {it.hero_lines} lines</span> : null}</td>
              <td><button type="button" {...q('retail_by_type')}>{aud(mode === 'ex' ? it.charged_ex : it.charged_inc)}</button></td>
              <td><button type="button" {...q(it.key === 'calls' ? 'hero_calling' : 'hero_lines')} title={nzd(mode === 'ex' ? it.hero_nzd_ex : it.hero_nzd_inc)}>
                {audAtRate(hero(it), rate)}</button></td>
              <td className={it.margin_aud != null && it.margin_aud < 0 ? 'is-down' : ''}>{audAtRate(it.margin_aud, rate)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td>{monthLong(period).split(' ')[0]}</td><td>{aud(mode === 'ex' ? data.total.charged_ex : data.total.charged_inc)}</td>
            <td>{audAtRate(hero(data.total), rate)}</td><td>{audAtRate(data.total.margin_aud, rate)}</td></tr>
          <tr className="fin-row-minus"><td>{data.cancelled.label}</td><td>{aud(mode === 'ex' ? data.cancelled.charged_ex : data.cancelled.charged_inc)}</td>
            <td></td><td>{audAtRate(data.cancelled.margin_aud, rate)}</td></tr>
          <tr className="fin-row-minus"><td>{data.trial_credit.label}</td><td>{aud(mode === 'ex' ? data.trial_credit.charged_ex : data.trial_credit.charged_inc)}</td>
            <td></td><td>{audAtRate(data.trial_credit.margin_aud, rate)}</td></tr>
          <tr><td>Collectable</td><td>{aud(mode === 'ex' ? data.collected.charged_ex : data.collected.charged_inc)}</td>
            <td></td><td>{audAtRate(data.collected.margin_aud, rate)}</td></tr>
        </tfoot>
      </table>
      <p className="fin-note">
        Outside charges applied: one-off charges {aud(b.one_off_ex)} · credits {aud(b.credits_other_ex)} ·
        trial credit {b.trial_credits ?? 0} × $11 = {aud(-(b.trial_credit_ex ?? 0))}. Margin is ex retail against Hero on the {data.cost_basis} basis.
      </p>
    </section>
  );
}

function Balances({ row, period, mode, q }: { row: FinanceMonth | null; period: string; mode: Mode; q: Qf }) {
  const { ladder, noLadder } = debtSplit(row);
  return (
    <section className="fin-panel">
      <h2>Balances at month end</h2>
      {!balancesObserved(period) || row?.prepaid_liability_inc == null ? (
        <p className="fin-note">Not observed: balances begin 22 Sep 2026, v2's first poll.</p>
      ) : (
        <div className="fin-lines">
          <div><span>Prepaid held <span className="fin-muted">(owed to customers)</span></span>
            <button type="button" {...q('balances')}>{aud(row.prepaid_liability_inc)}</button></div>
          <div><span>Debt, in a ladder</span><span className="is-down mono">{aud(ladder)}</span></div>
          <div><span>Debt, no ladder <span className="fin-muted">(mostly cancelled)</span></span><span className="is-down mono">{aud(noLadder)}</span></div>
          <div className="fin-rule" />
          <div><span>Trial credit issued</span><span className="mono">{row.trial_credits ?? 0} × $11 = {aud(-(row.trial_credit_ex ?? 0))}</span></div>
        </div>
      )}
      <p className="fin-note">{mode === 'ex' ? 'Balances are reported inc GST only. ' : ''}Observed from 22 Sep 2026; earlier months have no balance record.</p>
    </section>
  );
}

function Customers({ data, row, q }: { data: FinanceCustomers | null; row: FinanceMonth | null; q: Qf }) {
  const mv = data?.movement;
  const n = (x: number | null | undefined, sign = '') => (x == null ? '?' : `${sign}${x}`);
  return (
    <section className="fin-panel">
      <h2>Customers</h2>
      <div className="fin-move mono">
        <div><b>{n(mv?.start)}</b><span>start</span></div>
        <div className="is-up"><b>{n(mv?.converted, '+')}</b><span>converted</span></div>
        <div className="is-down"><b>{n(mv?.cancelled, '−')}</b><span>cancelled</span></div>
        <div><b>{n(mv?.end ?? row?.paying_active)}</b><span>end</span></div>
      </div>
      {mv?.other ? <p className="fin-note">{mv.other > 0 ? '+' : '−'}{Math.abs(mv.other)} by other changes of status.</p> : null}
      {mv?.start == null && <p className="fin-note">No start: the month before was not observed.</p>}
      {data && (
        <div className="fin-top">
          <div className="fin-label mono">Top 3 by charges{data.top_share != null ? ` · ${Math.round(data.top_share * 100)}% of charges` : ''}</div>
          {data.top.map((t) => (
            <div key={t.account_no}><span>{t.name ?? t.account_no}</span>
              <button type="button" {...q('top_accounts')}>{aud(t.charges_ex)}{t.in_arrears ? <span className="is-down"> · in arrears</span> : null}</button></div>
          ))}
        </div>
      )}
    </section>
  );
}

function Trials({ data, q }: { data: FinanceCustomers | null; q: Qf }) {
  const t = data?.trials;
  if (!t) return <section className="fin-panel"><h2>Trial economics · 6 months</h2><p className="list-empty">Reading…</p></section>;
  const payback = t.cost_per_conversion_aud != null && t.arpu_aud_ex ? Math.ceil(t.cost_per_conversion_aud / t.arpu_aud_ex) : null;
  return (
    <section className="fin-panel">
      <h2>Trial economics · 6 months</h2>
      <div className="fin-lines">
        <div><span>Signups</span><button type="button" {...q('trial_cohort')}>{t.signups}</button></div>
        <div><span>Converted</span><span className="mono">{t.converted} <span className="fin-muted">({t.conversion == null ? '?' : Math.round(t.conversion * 100)}%)</span></span></div>
        <div><span>Trial credit issued</span><span className="mono">{aud(t.trial_credit_ex)}</span></div>
        <div><span>Trial credit consumed</span><span className="mono">{aud(t.trial_credit_consumed_ex)}</span></div>
        <div><span>Hero cost of unpaid trials</span><button type="button" {...q('trial_hero_cost')}><span className="is-down">{aud(t.hero_cost_unpaid_aud)}</span></button></div>
        <div className="fin-rule" />
        <div className="fin-strong"><span>Cost per conversion</span><span className="mono">{aud(t.cost_per_conversion_aud)}</span></div>
      </div>
      <p className="fin-note">
        {payback != null ? `Against an ARPU of ${aud(t.arpu_aud_ex)}, a conversion pays back in ${payback} month${payback > 1 ? 's' : ''}. ` : ''}
        Hero cost is {t.hero_cost_note}.{t.hero_cost_unrated_nzd ? ` ${nzd(t.hero_cost_unrated_nzd)} had no rate.` : ''}
      </p>
    </section>
  );
}

function Audit({ data }: { data: ByItem | null }) {
  const items = data?.audit ?? [];
  const open = items.filter((a) => a.status === 'pending');
  return (
    <section className={`fin-panel ${open.length ? 'is-alert' : ''}`}>
      <div className="fin-panel-head"><h2>Wholesale audit</h2><span className="mono fin-small">{open.length} open of {items.length}</span></div>
      {items.length === 0 ? <p className="fin-note">No findings for this month.</p> : (
        <ul className="fin-audit">
          {items.slice(0, 6).map((a) => (
            <li key={a.id}><span className={a.status === 'pending' ? 'is-down' : 'is-up'}>●</span><span>{a.title}</span></li>
          ))}
        </ul>
      )}
      <Link href={`/finance/audit${data ? `?period=${data.period}` : ''}`} className="fin-link">Open the audit →</Link>
    </section>
  );
}

function Footer({ data, row, onConfirm, onChanged, q }: {
  data: FinanceStatement | null; row: FinanceMonth | null; onConfirm: (r: ConfirmRequest) => void; onChanged: () => void; q: Qf;
}) {
  const [form, setForm] = useState({ effective_date: '', aud_spent: '', nzd_received: '', source: '' });
  const [error, setError] = useState<string | null>(null);
  const audN = Number(form.aud_spent); const nzdN = Number(form.nzd_received);
  const ready = /^\d{4}-\d{2}-\d{2}$/.test(form.effective_date) && audN > 0 && nzdN > 0;
  const submit = () => onConfirm({
    title: `Record ${nzd(nzdN)} bought for ${aud(audN, { cents: true })}?`,
    detail: `${(audN / nzdN).toFixed(4)} AUD per NZD from ${form.effective_date}. Every Hero month invoiced on or after that date `
      + 'uses it until a later rate; the figures are rebuilt now. Recorded with your name.',
    confirmLabel: 'Record rate',
    onConfirm: async () => {
      setError(null);
      try {
        await api.finance.addFxRate({ effective_date: form.effective_date, aud_spent: audN, nzd_received: nzdN, source: form.source || undefined });
        setForm({ effective_date: '', aud_spent: '', nzd_received: '', source: '' });
        onChanged();
      } catch (e) { setError((e as Error).message); }
    },
  });
  return (
    <footer className="fin-foot">
      <div className="fin-statement">
        <div className="fin-label mono">Hero statement · {row ? monthLong(row.month) : ''}</div>
        {data?.invoices.map((l) => (
          <div key={l.reference}><button type="button" {...q('statement')}>Invoice {l.posted_at.slice(0, 10)} · {nzd(l.nzd)} · ref {l.reference}</button>
            <span className={l.matches ? 'is-up' : 'is-down'}>{l.matches ? ' · equals the period total' : ` · period total ${nzd(l.period_total_nzd ?? null)}`}</span></div>
        ))}
        {data && !data.invoices.length && <div className="fin-muted">No invoice yet for this month.</div>}
        {data?.payments.map((l) => <div key={l.reference}>Paid {l.posted_at.slice(0, 10)} · {nzd(l.nzd)} · ref {l.reference}</div>)}
        <div className="fin-muted">Rate in use: {rateLabel(row?.fx_rate ?? null, row?.fx_source ?? null, row?.fx_effective_date ?? null)}</div>
      </div>
      <form className="fin-fx" onSubmit={(e) => { e.preventDefault(); if (ready) submit(); }}>
        <div className="fin-label mono">Add FX rate</div>
        <input type="date" aria-label="Date" value={form.effective_date} onChange={(e) => setForm({ ...form, effective_date: e.target.value })} />
        <input inputMode="decimal" placeholder="AUD spent" aria-label="AUD spent" value={form.aud_spent} onChange={(e) => setForm({ ...form, aud_spent: e.target.value })} />
        <input inputMode="decimal" placeholder="NZD received" aria-label="NZD received" value={form.nzd_received} onChange={(e) => setForm({ ...form, nzd_received: e.target.value })} />
        <input placeholder="source (bank, date)" aria-label="Source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} />
        <button type="submit" disabled={!ready}>Add FX rate</button>
        {ready && <span className="fin-muted mono">{(audN / nzdN).toFixed(4)} AUD/NZD</span>}
        {error && <span className="is-down">{error}</span>}
      </form>
      <p className="fin-basis mono">
        {data?.basis_note ?? ''} Rebuilt nightly 03:00 Brisbane · as of {row ? whenLocal(row.as_of) : '—'} · tap any figure for its query.
        {row?.source_notes && row.source_notes !== 'complete' ? ` Notes: ${row.source_notes}.` : ''}
      </p>
    </footer>
  );
}

/** No figures yet: the ledger builds them at 03:05 Brisbane, or now, on the operator's click — the same run. */
function BuildNow({ onBuilt }: { onBuilt: () => void }) {
  const [state, setState] = useState<'idle' | 'building' | string>('idle');
  const build = async () => {
    setState('building');
    try { await api.finance.refresh(); setState('idle'); onBuilt(); } catch (e) { setState((e as Error).message); }
  };
  return (
    <div className="list-empty">
      <p>No finance figures yet: the ledger builds them every night at 03:05 Brisbane.</p>
      <p className="fin-sub">Building now reads Hero's bill and Stripe, makes the figures, and runs the audit and the alerts — their cards
        arrive on the Desk. About 20 seconds.</p>
      <button type="button" className="fin-build" onClick={build} disabled={state === 'building'}>
        {state === 'building' ? 'Building…' : 'Build the figures now'}
      </button>
      {state !== 'idle' && state !== 'building' && <p className="is-down">{state}</p>}
    </div>
  );
}

